import Image from 'next/image'

import heartFilledImage from 'assets/heart-1.png'
import heartEmptyImage from 'assets/heart-2.png'

import { Authorization } from '@/widgets/Authorization'

import { Button, Modal } from '@/shared/ui'

import { ProductReview, ProductReviewVote, ReviewMediaItem } from '../model/types'

import { MediaCatalogueModal } from './media-catalogue-modal/MediaCatalogueModal'
import { MediaGalleryModal } from './media-gallery/MediaGalleryModal'
import { ModerationRulesModal } from './moderation-rules-modal'
import { ReviewForm } from './review-form/ReviewForm'

import styles from './ProductReviewsModals.module.scss'

type VoteRequest = (reviewId: string, vote: ProductReviewVote) => Promise<ProductReview | undefined> | undefined

type ProductReviewsModalsProps = {
	productCode: number
	productName: string
	token?: string
	onVote: VoteRequest
	reviewForm: {
		isOpen: boolean
		close: () => void
		handleSubmitted: (rating: number, review?: ProductReview) => void
	}
	editReview: {
		review: ProductReview | null
		close: () => void
		handleSubmitted: (rating: number, review?: ProductReview) => void
	}
	authorization: {
		isOpen: boolean
		close: () => void
	}
	moderation: {
		isOpen: boolean
		open: () => void
		close: () => void
		openFromSuccess: () => void
	}
	success: {
		rating: number | null
		close: () => void
	}
	media: {
		items: ReviewMediaItem[]
		total: number
		initialIndex: number
		isOpen: boolean
		close: () => void
		loadMore: () => Promise<void>
		isLoadingMore: boolean
	}
	mediaCatalogue: {
		isOpen: boolean
		close: () => void
		openMedia: (index: number) => void
		hasMore: boolean
	}
}

export const ProductReviewsModals = ({
	productCode,
	productName,
	token,
	onVote,
	reviewForm,
	editReview,
	authorization,
	moderation,
	success,
	media,
	mediaCatalogue
}: ProductReviewsModalsProps) => (
	<>
		<Modal
			className={styles.reviewModalRoot}
			contentClassName={styles.reviewModal}
			isOpen={reviewForm.isOpen}
			onClose={reviewForm.close}>
			<ReviewForm
				productCode={productCode}
				productName={productName}
				token={token}
				onOpenModerationRules={moderation.open}
				onSuccess={reviewForm.handleSubmitted}
			/>
		</Modal>

		<Modal fullScreenOnMobile isOpen={authorization.isOpen} onClose={authorization.close} order={2}>
			<Authorization onSuccess={authorization.close} />
		</Modal>

		<Modal
			className={styles.reviewModalRoot}
			contentClassName={styles.reviewModal}
			isOpen={editReview.review !== null}
			onClose={editReview.close}>
			{editReview.review && (
				<ReviewForm
					productCode={productCode}
					productName={productName}
					token={token}
					review={editReview.review}
					onOpenModerationRules={moderation.open}
					onSuccess={editReview.handleSubmitted}
				/>
			)}
		</Modal>

		<ModerationRulesModal open={moderation.isOpen} onClose={moderation.close} />

		<Modal contentClassName={styles.reviewSuccessModal} isOpen={success.rating !== null} onClose={success.close}>
			<div className={styles.reviewSuccessContent}>
				<span className={styles.reviewSuccessIcon} aria-hidden="true">
					<Image
						src={success.rating !== null && success.rating >= 4 ? heartFilledImage : heartEmptyImage}
						width={63}
						height={63}
						alt=""
					/>
				</span>
				<h2>{success.rating !== null && success.rating >= 4 ? 'Спасибо за отзыв!' : 'В сердечке пусто!'}</h2>
				<p>
					Отзыв отправлен на проверку, скоро мы его опубликуем согласно{' '}
					<button type="button" onClick={moderation.openFromSuccess}>
						правилам
					</button>
				</p>
				<Button fullWidth onClick={success.close}>
					Закрыть
				</Button>
			</div>
		</Modal>

		<MediaGalleryModal
			items={media.items}
			total={media.total}
			initialIndex={media.initialIndex}
			open={media.isOpen}
			onClose={media.close}
			onLoadMore={media.loadMore}
			isLoadingMore={media.isLoadingMore}
			onVote={onVote}
			canVoteOptimistically={Boolean(token)}
		/>
		<MediaCatalogueModal
			items={media.items}
			open={mediaCatalogue.isOpen}
			onClose={mediaCatalogue.close}
			onOpenMedia={mediaCatalogue.openMedia}
			onLoadMore={media.loadMore}
			isLoadingMore={media.isLoadingMore}
			hasMore={mediaCatalogue.hasMore}
		/>
	</>
)
